import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';

// Types
import { FormData, ProductType, ConsentState, SingleAnswerSlide, FormInputSlide } from './types';

// Hooks
import { useSliderNavigation, useZipCodeCheck, useSavingsCalculator } from './hooks';

// Config
import { BENEFIT_SLIDE_IDS } from './config/slidesConfig';

// Components
import ProgressBar from './components/ProgressBar';
import SlideHeader from './components/SlideHeader';
import ChoiceGrid from './components/ChoiceGrid';
import FormInput from './components/FormInput';
import SuccessSlide from './components/SuccessSlide';
import {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
} from '../shared/consent/OptionalConsents';

// Utils
import { submitLead } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, splitFullName } from './utils/helpers';

/**
 * SliderPnrrCerEnpal - Componente principale del wizard di registrazione
 * Variante con configurazione PNRR CER Enpal
 */
const SliderPnrrCerEnpal: React.FC = () => {
  // Form data state
  const [formData, setFormData] = useState<FormData>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // Lead state
  const [mongoId, setMongoId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  // Navigation hook
  const {
    currentSlide,
    progress,
    goNext,
    goPrev,
    goToSlideById,
    canGoPrev,
  } = useSliderNavigation(formData);

  // ZIP check hook
  const { zipFlag, checkZip } = useZipCodeCheck();

  // Savings calculator hook
  const { tooltipText, getBenefitText, getTotalSavings } = useSavingsCalculator(
    formData.product_type as ProductType,
    zipFlag,
    formData.zip
  );

  // Track initial source
  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  // Update form data with a new value
  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle single answer selection
  const handleAnswerSelect = useCallback(
    (slide: SingleAnswerSlide, value: string) => {
      updateFormData(slide.apiFieldName, value);
      trackSlideAnswer(slide.id, slide.title || '', value);

      if (slide.autoAdvance) {
        setTimeout(() => goNext(), 200);
      }
    },
    [updateFormData, goNext]
  );

  // Handle form input change
  const handleFormInputChange = useCallback(
    (name: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [name]: value }));

      // Special handling for ZIP code
      if (name === 'zip') {
        checkZip(value);
        updateFormData('zip', value);
      }
    },
    [checkZip, updateFormData]
  );

  // Handle form submission for input slides
  const handleFormSubmit = useCallback(
    async (slide: FormInputSlide) => {
      // Copy form values to form data
      Object.entries(formValues).forEach(([key, value]) => {
        updateFormData(key, value);
      });

      trackSlideAnswer(slide.id, slide.title || '', 'continue_button');

      // If this is the phone form, submit the lead
      if (slide.id === 'phone_form') {
        await handleLeadSubmit();
      } else {
        goNext();
      }
    },
    [formValues, updateFormData, goNext]
  );

  // Submit lead to API
  const handleLeadSubmit = useCallback(async () => {
    setIsLoading(true);
    goToSlideById('loading');

    const uid = uuidv4();

    // Merge form values with form data
    const finalFormData: FormData = {
      ...formData,
      ...formValues,
    };

    try {
      const id = await submitLead(finalFormData, uid, consentForApi);

      if (id) {
        setMongoId(id);
        trackSliderSuccess();

        // Track lead with hashed email
        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }

        goToSlideById('success');
      } else {
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      goToSlideById('error');
    } finally {
      setIsLoading(false);
    }
  }, [formData, formValues, consentForApi, goToSlideById]);


  // Handle error retry
  const handleRetry = useCallback(() => {
    goToSlideById('customer_type');
  }, [goToSlideById]);

  // Render current slide content
  const renderSlideContent = () => {
    if (!currentSlide) return null;

    switch (currentSlide.type) {
      case 'SINGLE_ANSWER': {
        const slide = currentSlide as SingleAnswerSlide;
        const isBenefitSlide = BENEFIT_SLIDE_IDS.includes(slide.id as typeof BENEFIT_SLIDE_IDS[number]);
        const benefitText = getBenefitText(slide.id);
        const totalSavings = getTotalSavings(slide.id);

        return (
          <>
            {/* Header with benefits for decision_stage */}
            {slide.header && (
              <SlideHeader
                savingsSubText={slide.header.savingsSubText}
                savingsText={benefitText || ''}
                tooltipText={tooltipText}
                zipCode={formData.zip}
                totalSavings={isBenefitSlide ? totalSavings : undefined}
              />
            )}

            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{slide.title}</h3>

              <ChoiceGrid
                answers={slide.answers}
                selectedValue={formData[slide.apiFieldName]}
                onSelect={(value) => handleAnswerSelect(slide, value)}
                columns={slide.id === 'install_timeline' || slide.id === 'decision_stage' ? 2 : 3}
              />

              {!slide.autoAdvance && (
                <div className={styles.buttonGroup}>
                  {canGoPrev && (
                    <button type="button" className={styles.btnPrev} onClick={goPrev}>
                      Indietro
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.btnNext}
                    onClick={goNext}
                    disabled={!formData[slide.apiFieldName]}
                  >
                    Avanti
                  </button>
                </div>
              )}
            </div>
          </>
        );
      }

      case 'FORM_INPUT': {
        const slide = currentSlide as FormInputSlide;
        const isBenefitSlide = BENEFIT_SLIDE_IDS.includes(slide.id as typeof BENEFIT_SLIDE_IDS[number]);
        const benefitText = getBenefitText(slide.id);
        const totalSavings = getTotalSavings(slide.id);
        const isPhoneSlide = slide.id === 'phone_form';

        return (
          <>
            {slide.header && (
              <SlideHeader
                savingsSubText={slide.header.savingsSubText}
                savingsText={benefitText || ''}
                tooltipText={tooltipText}
                zipCode={formData.zip}
                totalSavings={isBenefitSlide ? totalSavings : undefined}
              />
            )}

            <div className={styles.slideBody}>
              {slide.title && <h3 className={styles.wizardTitle}>{slide.title}</h3>}

              <FormInput
                inputs={slide.inputs}
                values={formValues}
                onChange={handleFormInputChange}
                onSubmit={() => handleFormSubmit(slide)}
                submitLabel={isPhoneSlide ? 'Scopri il risparmio totale' : 'Avanti'}
                showBackButton={canGoPrev}
                onBack={goPrev}
                isPhoneSlide={isPhoneSlide}
                optionalConsents={isPhoneSlide ? optionalConsents : undefined}
                onOptionalConsentsChange={
                  isPhoneSlide ? setOptionalConsents : undefined
                }
              />
            </div>
          </>
        );
      }

      case 'INFO': {
        if (currentSlide.id === 'loading') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Attendere prego...</p>
              <div className={styles.loadingSpinner} />
            </div>
          );
        }

        if (currentSlide.id === 'success') {
          return (
            <SuccessSlide
              productType={(formData.product_type || 'Photovoltaic') as ProductType}
              zipFlag={zipFlag}
              zipCode={formData.zip}
              mongoId={mongoId}
              phone={formData.phone}
            />
          );
        }

        if (currentSlide.id === 'error') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Si è verificato un errore...</p>
              <button
                type="button"
                className={styles.btnNext}
                onClick={handleRetry}
                style={{ marginTop: '1rem', maxWidth: '200px' }}
              >
                Riprova
              </button>
            </div>
          );
        }

        return (
          <div className={styles.slideBody}>
            {currentSlide.title && <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>}
            {currentSlide.html && (
              <div dangerouslySetInnerHTML={{ __html: currentSlide.html }} />
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <section className={styles.sliderSection}>
      <div className={styles.sliderContainer}>
        {/* <div className={styles.sliderHeaderText}>
          <h2>Scopri in 30 secondi quanto riceveresti di rimborso</h2>
          <p>Tramite gli incentivi statali ed europei previsti dal PNRR.</p>
        </div> */}

        <div className={styles.sliderWrapper}>
          <ProgressBar progress={progress} />

          <div className={styles.wizSlide}>
            {renderSlideContent()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SliderPnrrCerEnpal;

